/**
 * ArubaGroupManagementPage — view + manage Aruba Central groups.
 *
 * Today this is read-only with a quick "compliance" indicator (whether the
 * group is on the GuestTek-blessed AOS baseline). Clicking a group opens an
 * inline detail panel with allowed device families.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle2,
  Search,
  ShieldCheck,
  ShieldAlert,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardSub, CardTitle } from "@/components/ui/card";
import { CardGridSkeleton } from "@/components/ui/skeleton";
import { ArubaSubNav } from "@/components/app/aruba-sub-nav";
import { NoVendorKeysBanner } from "@/components/app/no-vendor-keys-banner";
import { useArubaGroups } from "@/stores/aruba-groups";
import { useUiAutoAnimate } from "@/components/ui/auto-animate";

export function ArubaGroupManagementPage() {
  const animatedRootRef = useUiAutoAnimate();

  const { data, error, status, load } = useArubaGroups();
  useEffect(() => {
    void load();
  }, [load]);

  const [query, setQuery] = useState("");
  const [showNonCompliant, setShowNonCompliant] = useState(false);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return data.filter((g) => {
      if (showNonCompliant && g.compliant !== false) return false;
      if (!q) return true;
      return (
        g.name.toLowerCase().includes(q) ||
        (g.architecture ?? "").toLowerCase().includes(q) ||
        (g.aosVersion ?? "").toLowerCase().includes(q)
      );
    });
  }, [data, query, showNonCompliant]);

  // Cap visible cards at MAX_ROWS — a real Aruba tenant can have hundreds
  // (apigw-ca returns 734) and rendering them all in a 2-col grid jank-paints
  // for several seconds. Same nudge-to-filter pattern as manage-devices.
  const MAX_ROWS = 200;
  const visible = filtered.slice(0, MAX_ROWS);
  const overflow = filtered.length - visible.length;

  const compliantCount = data.filter((g) => g.compliant !== false).length;

  return (
    <div ref={animatedRootRef} className="flex flex-col gap-6">
      <div className="flex items-center gap-2 text-xs text-fg-mute">
        <Link to="/" className="text-fg-dim hover:text-fg">
          Operations
        </Link>
        <span className="opacity-40">/</span>
        <Link to="/aruba/keys" className="text-fg-dim hover:text-fg">
          Aruba
        </Link>
        <span className="opacity-40">/</span>
        <span className="text-fg">Group management</span>
      </div>

      <ArubaSubNav />

      <NoVendorKeysBanner vendor="aruba" />

      <div className="flex items-end justify-between gap-6">
        <div>
          <h1 className="m-0 text-3xl font-extrabold tracking-tight text-aurora">
            Group management
          </h1>
          <p className="mt-1.5 max-w-[680px] text-sm text-fg-dim">
            Aruba Central groups are the unit of config templating. Groups
            marked non-compliant have drifted off the GuestTek-blessed AOS
            baseline.
          </p>
        </div>
        <Badge
          variant={compliantCount === data.length ? "ok" : "warn"}
          size="md"
        >
          {compliantCount === data.length ? (
            <ShieldCheck size={11} />
          ) : (
            <ShieldAlert size={11} />
          )}
          {compliantCount}/{data.length} compliant
        </Badge>
      </div>

      {error ? (
        <output
          className="flex items-center gap-2.5 rounded-xl border border-peach/30 bg-peach/10 px-3.5 py-2.5 text-sm text-peach"
        >
          <AlertTriangle size={16} />
          <span>
            <b className="font-semibold">Showing fixture data.</b>{" "}
            <span className="text-peach/80">{error}</span>
          </span>
        </output>
      ) : null}

      <Card>
        <CardContent className="flex items-center gap-3">
          <div className="relative max-w-md flex-1">
            <Search
              size={14}
              className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-fg-mute"
            />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter by name, architecture, AOS version…"
              className="pl-9"
            />
          </div>
          <Button
            variant={showNonCompliant ? "primary" : "ghost"}
            size="sm"
            onClick={() => setShowNonCompliant((v) => !v)}
          >
            <ShieldAlert size={13} />
            {showNonCompliant ? "Drifted only · on" : "Drifted only · off"}
          </Button>
          <span className="ml-auto font-mono text-xs text-fg-mute">
            {filtered.length}/{data.length} match
            {overflow > 0 ? <> · capped to first {MAX_ROWS}</> : null}
          </span>
        </CardContent>
      </Card>

      {status === "loading" && data.length === 0 ? (
        <CardGridSkeleton cards={4} />
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {visible.map((g) => (
            <Card key={g.name}>
              <CardContent className="flex flex-col gap-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <CardTitle className="truncate">{g.name}</CardTitle>
                    <CardSub className="font-mono">
                      {g.architecture ?? "—"} · {g.aosVersion ?? "—"}
                    </CardSub>
                  </div>
                  {g.compliant === false ? (
                    <Badge variant="warn" size="md">
                      drifted
                    </Badge>
                  ) : (
                    <Badge variant="ok" size="md">
                      <CheckCircle2 size={11} />
                      compliant
                    </Badge>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-2 text-xs text-fg-mute">
                  <span>
                    <b className="font-mono text-fg">{g.deviceCount ?? 0}</b>{" "}
                    devices
                  </span>
                  {(g.allowedDevices ?? []).length > 0 ? (
                    <>
                      <span>·</span>
                      <span>allows</span>
                      {(g.allowedDevices ?? []).map((d) => (
                        <Badge key={d} variant="neutral" size="sm">
                          {d}
                        </Badge>
                      ))}
                    </>
                  ) : null}
                </div>

                <div className="flex justify-end gap-2 border-t border-line pt-3">
                  <Button size="sm" variant="ghost">
                    Edit baseline
                  </Button>
                  <Button size="sm">Inspect →</Button>
                </div>
              </CardContent>
            </Card>
          ))}
          {visible.length === 0 ? (
            <Card>
              <CardContent className="py-10 text-center text-sm text-fg-mute">
                {query ? `No groups match "${query}".` : "No groups available."}
              </CardContent>
            </Card>
          ) : null}
          {overflow > 0 ? (
            <Card className="lg:col-span-2">
              <CardContent className="py-3 text-center text-[11px] text-fg-mute">
                {overflow.toLocaleString()} more match — refine the filter to
                see them.
              </CardContent>
            </Card>
          ) : null}
        </div>
      )}
    </div>
  );
}
